import React, {
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { LayoutContext } from '@rsos/components/capstone/base/Layout';
import {
  TEST_TRAIN_DERAILMENT,
  TRAIN_DERAILMENT,
} from '@rsos/constants/alertTypes';
import { BP_LAPTOP } from '@rsos/constants/breakpoints';
import useIsIRP from '@rsos/utils/useIsIRP';
import Loader from '../Loader';
import AlertDataCardNotification from './AlertDataCardNotification/AlertDataCardNotification';
import CustomRenderer, { CustomCard } from './CustomRenderer';
import BPLaptopDataCardHeader from './DataCardBPLaptopHeader';
import DataCardBody from './DataCardBody';
import DataCardHeader from './DataCardHeader';
import ShareIncidentModal from './ShareIncident/ShareIncident';
import TestAlertBanner from './TestAlertBanner';

export const StyledLoader = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
`;
const emergencyTypesToShare = [TRAIN_DERAILMENT, TEST_TRAIN_DERAILMENT];

const useHeaderHeight = (headerRef, deps = []) => {
  const [headerHeight, setHeaderHeight] = useState(0);
  useEffect(() => {
    if (!headerRef.current) {
      return;
    }
    const height = headerRef.current.clientHeight;
    setHeaderHeight(height);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerRef, ...deps]);
  return headerHeight;
};

const useCardPadding = cardRef => {
  const [cardPadding, setCardPadding] = useState(0);
  useLayoutEffect(() => {
    if (!cardRef.current) {
      return;
    }
    const padding = window
      .getComputedStyle(cardRef.current, null)
      .getPropertyValue('padding');
    const paddingNumber = parseFloat(padding);
    setCardPadding(paddingNumber);
  }, [cardRef]);
  return cardPadding;
};

const DataCard = ({
  accessToken,
  onClose,
  height,
  isCentralStation,
  alert: currentAlert,
  adrData: currentAdrData,
}) => {
  const cardRef = useRef();
  const headerRef = useRef();
  const cardBodyRef = useRef();
  const headerHeight = useHeaderHeight(headerRef);
  const cardPadding = useCardPadding(cardRef);

  const cardBodyHeight = height - (headerHeight + cardPadding);
  const emergencyType = currentAlert?.emergency_type;
  const isTestAlertRSP =
    emergencyType?.name?.startsWith('TEST') && !isCentralStation;
  const siteType = currentAlert?.site_type?.display_name;
  const alertTime =
    currentAlert?.location_history?.[0]?.created_at ||
    currentAlert.incident_time;
  const alertStatus = currentAlert?.status?.name;
  const serviceProviderName =
    currentAlert.service_provider_name &&
    (currentAlert.service_provider_name.display_name ||
      currentAlert.service_provider_name);
  const { layout } = useContext(LayoutContext);
  const { isIRPresponsiveConditional } = useIsIRP();
  const responsiveHeader =
    !isCentralStation &&
    layout?.window?.width <= BP_LAPTOP &&
    !isIRPresponsiveConditional;

  const [isShareIncidentModalOpen, setIsShareIncidentModalOpen] = useState(
    false,
  );
  const handleCloseModal = () => {
    setIsShareIncidentModalOpen(false);
  };
  const isShareIncidentAvailable =
    emergencyTypesToShare.includes(emergencyType?.name) && !isCentralStation;
  return (
    <>
      {responsiveHeader ? (
        <BPLaptopDataCardHeader
          onClose={onClose}
          alertStatus={alertStatus}
          emergencyType={emergencyType}
          siteType={siteType}
          serviceProviderName={serviceProviderName}
          setIsShareIncidentModalOpen={setIsShareIncidentModalOpen}
          isShareIncidentAvailable={isShareIncidentAvailable}
        />
      ) : (
        <DataCardHeader
          ref={headerRef}
          onClose={() => onClose()}
          alertStatus={alertStatus}
          emergencyType={emergencyType}
          siteType={siteType}
          serviceProviderName={serviceProviderName}
          setIsShareIncidentModalOpen={setIsShareIncidentModalOpen}
          isCentralStation={isCentralStation}
          isShareIncidentAvailable={isShareIncidentAvailable}
        />
      )}
      {isTestAlertRSP && <TestAlertBanner />}
      {!isCentralStation && <AlertDataCardNotification alert={currentAlert} />}
      <DataCardBody height={cardBodyHeight} ref={cardBodyRef}>
        {currentAdrData ? (
          <CustomRenderer
            accessToken={accessToken}
            siteType={siteType}
            emergencyType={emergencyType}
            serviceProviderName={serviceProviderName}
            data={currentAdrData.data}
            location={currentAlert.location}
            time={alertTime}
          />
        ) : (
          <CustomCard
            time={alertTime}
            location={currentAlert.location}
            serviceProviderName={serviceProviderName}
            emergencyType={emergencyType}
            siteType={siteType}
          >
            <StyledLoader>
              <Loader />
            </StyledLoader>
          </CustomCard>
        )}
      </DataCardBody>
      {isShareIncidentAvailable && (
        <ShareIncidentModal
          isOpened={isShareIncidentModalOpen}
          onClose={handleCloseModal}
          alert={currentAlert}
        />
      )}
    </>
  );
};

export const AlertPropType = PropTypes.shape({
  emergency_type: PropTypes.shape({
    display_name: PropTypes.string.isRequired,
  }).isRequired,
  site_type: PropTypes.shape({
    display_name: PropTypes.string.isRequired,
  }),
  location: PropTypes.shape().isRequired,
  incident_time: PropTypes.string.isRequired,
});

export const AdrDataPropType = PropTypes.shape({
  body: PropTypes.shape({}).isRequired,
  display_schema: PropTypes.shape({}).isRequired,
});

DataCard.propTypes = {
  onClose: PropTypes.func.isRequired,
  alert: AlertPropType.isRequired,
  adrData: AdrDataPropType.isRequired,
};

export default DataCard;
